import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Scale, 
  Calculator, 
  AlertTriangle, 
  CheckCircle, 
  TrendingDown,
  Info,
  Save,
  RefreshCw,
  History,
  Clock,
  Calendar,
  Download
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { usePageVisibility } from '@/hooks/usePageVisibility';
import { format } from 'date-fns';
import { 
  calculateWasteUsingRestockCycles,
  validateWasteCalculationData
} from '@/utils/wasteCalculationUtils';

interface Material {
  id: string;
  name: string;
  unit_type: string;
  current_stock: number;
  min_threshold: number;
  cost_per_unit: number;
  supplier?: string | null;
}

interface PhysicalCountData {
  material_id: string;
  actual_stock: number;
  expected_stock: number;
  waste_amount: number;
  waste_percentage: number;
  notes: string;
}

interface PhysicalCountRecord {
  id: string;
  material_id: string;
  expected_stock: number;
  actual_stock: number;
  waste_amount: number;
  waste_percentage: number;
  notes: string;
  counted_at: string;
  user_id: string;
  branch_id: string | null;
  material_name?: string;
  unit_type?: string;
}

interface PhysicalCountPageProps {
  selectedBranchId: string;
  onCountCompleted?: () => void;
}

export function PhysicalCountPage({ 
  selectedBranchId,
  onCountCompleted 
}: PhysicalCountPageProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const isPageVisible = usePageVisibility();
  const [materials, setMaterials] = useState<Material[]>([]);
  const [countData, setCountData] = useState<Map<string, PhysicalCountData>>(new Map());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [recipes, setRecipes] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('count');
  const [historyRecords, setHistoryRecords] = useState<PhysicalCountRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [timeFilter, setTimeFilter] = useState('30');
  const [materialFilter, setMaterialFilter] = useState('all');
  const previousBranchIdRef = useRef<string | undefined>();
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (user && selectedBranchId && isPageVisible) {
      const branchChanged = previousBranchIdRef.current !== selectedBranchId;
      if (!hasInitializedRef.current || branchChanged) {
        loadData();
        previousBranchIdRef.current = selectedBranchId;
        hasInitializedRef.current = true;
      }
    }
  }, [user, selectedBranchId, isPageVisible]);

  const loadData = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const [
        { data: materialsData },
        { data: transactionsData },
        { data: menuItemsData },
        { data: recipesData }
      ] = await Promise.all([
        (supabase as any).from('raw_materials').select('*').eq('user_id', user.id).eq('branch_id', selectedBranchId),
        (supabase as any).from('inventory_transactions').select('*').eq('user_id', user.id).eq('branch_id', selectedBranchId),
        (supabase as any).from('menu_items').select('id, name, category_id, categories!inner(branch_id)').eq('categories.branch_id', selectedBranchId),
        (supabase as any).from('recipes').select('*').eq('user_id', user.id).eq('branch_id', selectedBranchId)
      ]);

      const materialsArray = materialsData || [];
      setMaterials(materialsArray);
      setTransactions(transactionsData || []);
      setMenuItems(menuItemsData || []);
      setRecipes(recipesData || []);

      const wasteResult = calculateWasteUsingRestockCycles(
        materialsArray,
        transactionsData || [],
        menuItemsData || [],
        recipesData || [],
        { timeRangeDays: 365, includeCurrentStock: true }
      );

      const newCountData = new Map<string, PhysicalCountData>();
      
      materialsArray.forEach(material => {
        const expectedStock = material.current_stock;

        newCountData.set(material.id, {
          material_id: material.id,
          actual_stock: 0,
          expected_stock: expectedStock,
          waste_amount: 0,
          waste_percentage: 0,
          notes: ''
        });
      });

      setCountData(newCountData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load physical count data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleActualStockChange = (materialId: string, value: string) => {
    const actualStock = parseFloat(value) || 0;
    const currentData = countData.get(materialId);
    
    if (currentData) {
      const wasteAmount = currentData.expected_stock - actualStock;
      const wastePercentage = currentData.expected_stock > 0 
        ? (wasteAmount / currentData.expected_stock) * 100 
        : 0;

      const updatedData = {
        ...currentData,
        actual_stock: actualStock,
        waste_amount: wasteAmount,
        waste_percentage: wastePercentage
      };

      setCountData(new Map(countData.set(materialId, updatedData)));
    }
  };

  const handleNotesChange = (materialId: string, notes: string) => {
    const currentData = countData.get(materialId);
    if (currentData) {
      setCountData(new Map(countData.set(materialId, { ...currentData, notes })));
    }
  };

  const handleSaveCount = async () => {
    if (!user) return;

    setSaving(true);
    try {
      const countsToSave = Array.from(countData.values()).filter(
        data => data.actual_stock > 0 || data.notes.trim() !== ''
      );

      if (countsToSave.length === 0) {
        toast({
          title: 'No data to save',
          description: 'Please enter at least one physical count',
          variant: 'destructive'
        });
        return;
      }

      const recordsToInsert = countsToSave.map(data => ({
        user_id: user.id,
        branch_id: selectedBranchId,
        material_id: data.material_id,
        expected_stock: data.expected_stock,
        actual_stock: data.actual_stock,
        waste_amount: data.waste_amount,
        waste_percentage: data.waste_percentage,
        notes: data.notes,
        counted_at: new Date().toISOString()
      }));

      const { error } = await (supabase as any)
        .from('physical_count_records')
        .insert(recordsToInsert);

      if (error) throw error;

      for (const data of countsToSave) {
        const { error: updateError } = await (supabase as any)
          .from('raw_materials')
          .update({ current_stock: data.actual_stock })
          .eq('id', data.material_id);

        if (updateError) throw updateError;
      }

      toast({
        title: 'Success',
        description: `Physical count saved for ${countsToSave.length} material(s)`,
      });

      onCountCompleted?.();
      loadData();
    } catch (error) {
      console.error('Error saving count:', error);
      toast({
        title: 'Error',
        description: 'Failed to save physical count',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const loadHistory = async () => {
    if (!user) return;

    setHistoryLoading(true);
    try {
      const days = parseInt(timeFilter);
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      let query = (supabase as any)
        .from('physical_count_records')
        .select(`
          *,
          raw_materials (name, unit_type)
        `)
        .eq('user_id', user.id)
        .eq('branch_id', selectedBranchId)
        .gte('counted_at', startDate.toISOString())
        .order('counted_at', { ascending: false });

      if (materialFilter !== 'all') {
        query = query.eq('material_id', materialFilter);
      }

      const { data, error } = await query;

      if (error) throw error;

      const formattedRecords = (data || []).map((record: any) => ({
        ...record,
        material_name: record.raw_materials?.name,
        unit_type: record.raw_materials?.unit_type
      }));

      setHistoryRecords(formattedRecords);
    } catch (error) {
      console.error('Error loading history:', error);
      toast({
        title: 'Error',
        description: 'Failed to load count history',
        variant: 'destructive'
      });
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab, timeFilter, materialFilter]);

  const totalMaterials = materials.length;
  const countedMaterials = Array.from(countData.values()).filter(d => d.actual_stock > 0).length;
  const progress = totalMaterials > 0 ? (countedMaterials / totalMaterials) * 100 : 0;
  const totalWaste = Array.from(countData.values()).reduce((sum, d) => sum + d.waste_amount, 0);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
            <p className="text-sm text-muted-foreground">Loading materials...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="count" className="flex items-center gap-2">
            <Scale className="h-4 w-4" />
            Physical Count
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="count" className="space-y-4 mt-4">
          {/* Progress Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Count Progress</span>
                <Badge variant={countedMaterials === totalMaterials ? 'default' : 'secondary'}>
                  {countedMaterials}/{totalMaterials} Materials
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Progress value={progress} className="h-2" />
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Total Materials</p>
                  <p className="text-2xl font-bold">{totalMaterials}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Counted</p>
                  <p className="text-2xl font-bold text-green-600">{countedMaterials}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Total Waste</p>
                  <p className="text-2xl font-bold text-red-600">{totalWaste.toFixed(2)}</p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={handleSaveCount} disabled={saving || countedMaterials === 0} className="flex-1">
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Count'}
                </Button>
                <Button onClick={loadData} variant="outline" disabled={loading}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Materials Table */}
          <Card>
            <CardHeader>
              <CardTitle>Materials</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Material</TableHead>
                    <TableHead className="text-right">Expected Stock</TableHead>
                    <TableHead className="text-right">Actual Stock</TableHead>
                    <TableHead className="text-right">Difference</TableHead>
                    <TableHead className="text-right">Waste %</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {materials.map((material) => {
                    const data = countData.get(material.id);
                    if (!data) return null;

                    return (
                      <TableRow key={material.id}>
                        <TableCell className="font-medium">
                          {material.name}
                          <div className="text-xs text-muted-foreground">{material.unit_type}</div>
                        </TableCell>
                        <TableCell className="text-right">
                          {data.expected_stock.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number"
                            step="0.01"
                            value={data.actual_stock || ''}
                            onChange={(e) => handleActualStockChange(material.id, e.target.value)}
                            className="w-24 text-right"
                            placeholder="0.00"
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={data.waste_amount > 0 ? 'text-red-600 font-medium' : data.waste_amount < 0 ? 'text-green-600 font-medium' : ''}>
                            {data.waste_amount > 0 ? '-' : '+'}{Math.abs(data.waste_amount).toFixed(2)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant={data.waste_percentage > 10 ? 'destructive' : data.waste_percentage > 5 ? 'secondary' : 'default'}>
                            {data.waste_percentage.toFixed(1)}%
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Input
                            type="text"
                            value={data.notes}
                            onChange={(e) => handleNotesChange(material.id, e.target.value)}
                            placeholder="Notes..."
                            className="w-full"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Count History</CardTitle>
              <div className="flex gap-4">
                <Select value={timeFilter} onValueChange={setTimeFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={materialFilter} onValueChange={setMaterialFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="All materials" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All materials</SelectItem>
                    {materials.map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="text-center py-8">
                  <RefreshCw className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                </div>
              ) : historyRecords.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Material</TableHead>
                      <TableHead className="text-right">Expected</TableHead>
                      <TableHead className="text-right">Actual</TableHead>
                      <TableHead className="text-right">Waste</TableHead>
                      <TableHead className="text-right">Waste %</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {historyRecords.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell>
                          {format(new Date(record.counted_at), 'MMM dd, yyyy HH:mm')}
                        </TableCell>
                        <TableCell>{record.material_name}</TableCell>
                        <TableCell className="text-right">{record.expected_stock.toFixed(2)}</TableCell>
                        <TableCell className="text-right">{record.actual_stock.toFixed(2)}</TableCell>
                        <TableCell className="text-right">
                          <span className={record.waste_amount > 0 ? 'text-red-600' : 'text-green-600'}>
                            {record.waste_amount > 0 ? '-' : '+'}{Math.abs(record.waste_amount).toFixed(2)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant={record.waste_percentage > 10 ? 'destructive' : 'default'}>
                            {record.waste_percentage.toFixed(1)}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{record.notes || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No count history found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
